import React from "react";

/**
 * WindowScreenQuote logo mark.
 *
 * A confident, modern window-frame mark: a rounded-square containing a 2×2
 * window grid. The top-right pane is filled with the brand gold and stamped
 * with a white check — reading instantly as "your screen quote, approved /
 * locked in." Premium SaaS feel, distinctive shape, and the gold pane
 * doubles as a memorable single-glance identifier.
 *
 * Pass `withWordmark` to render the full lockup. `inverted` swaps the frame
 * color for use on dark backgrounds.
 */
export function Logo({ size = 36, withWordmark = false, inverted = false, className = "" }) {
  const frame = inverted ? "#FFFFFF" : "#0F172A";
  const pane = inverted ? "#0B1220" : "#FFFFFF";
  const accent = "#C75D33"; // brand copper
  const checkColor = inverted ? "#0B1220" : "#FFFFFF";

  const mark = (
    <svg viewBox="0 0 40 40" width={size} height={size} aria-hidden="true">
      <defs>
        <linearGradient id="wsq-frame-grad" x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor={inverted ? "#FFFFFF" : "#1E293B"} />
          <stop offset="1" stopColor={inverted ? "#E2E8F0" : "#0A0F1C"} />
        </linearGradient>
        <linearGradient id="wsq-accent-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#E07A52" />
          <stop offset="1" stopColor={accent} />
        </linearGradient>
      </defs>

      {/* Outer rounded-square frame */}
      <rect x="1.5" y="1.5" width="37" height="37" rx="10" fill="url(#wsq-frame-grad)" />

      {/* 2×2 window panes — clean, bold, no kindergarten dots */}
      <rect x="7.5" y="7.5" width="11" height="11" rx="2" fill={pane} />
      <rect x="21.5" y="7.5" width="11" height="11" rx="2" fill="url(#wsq-accent-grad)" />
      <rect x="7.5" y="21.5" width="11" height="11" rx="2" fill={pane} />
      <rect x="21.5" y="21.5" width="11" height="11" rx="2" fill={pane} />

      {/* "Approved / quoted" white check inside the gold pane */}
      <path
        d="M23.5 13.4 L26 15.9 L30 11.6"
        stroke={checkColor}
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />

      {/* Subtle inner-shadow band on top of frame for depth */}
      <rect x="1.5" y="1.5" width="37" height="37" rx="10" fill="none" stroke={inverted ? "rgba(0,0,0,0.18)" : "rgba(255,255,255,0.18)"} strokeWidth="1" />
    </svg>
  );

  if (!withWordmark) {
    return <span className={`inline-flex items-center ${className}`}>{mark}</span>;
  }

  return (
    <span className={`inline-flex items-center gap-2.5 ${className}`}>
      {mark}
      <span className="leading-[1.05] select-none">
        <span className={`font-display font-extrabold text-[15px] tracking-tight ${inverted ? "text-white" : "text-slate-900"}`}>WindowScreenQuote</span>
        <span className={`block text-[9px] uppercase tracking-[0.18em] font-bold -mt-px ${inverted ? "text-white/55" : "text-slate-400"}`}>Instant local pricing</span>
      </span>
    </span>
  );
}

export default Logo;
